import { Component } from '@theme/component';

// Manual choice belongs to this tab session, never a permanent device override.
const STORAGE_KEY = 'smartify-appearance-session-v1';
const CHANGE_EVENT = 'smartify:appearance-change';

/**
 * Keeps all header controls in sync with the document's prepaint appearance.
 * @typedef {object} AppearanceRefs
 * @property {HTMLButtonElement} button - Native toggle button.
 * @extends {Component<AppearanceRefs>}
 */
class SmartifyAppearance extends Component {
  requiredRefs = ['button'];
  #listeners;
  #media;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    document.addEventListener(CHANGE_EVENT, this.#sync, options);
    window.addEventListener('pageshow', this.#onPageShow, options);
    try {
      this.#media = window.matchMedia('(prefers-color-scheme: dark)');
      this.#media.addEventListener('change', this.#followSystem, options);
    } catch (error) {}
    this.#followSystem();
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /** Switches appearance; storage failure must not disable the current-page control. */
  handleToggle() {
    const root = document.documentElement;
    const mode = root.dataset.smartifyMode === 'dark' ? 'light' : 'dark';
    root.dataset.smartifyInteracted = 'true';
    root.dataset.smartifyPreference = mode;
    root.dataset.smartifyMode = mode;
    // A failed write still represents a deliberate choice for this page's lifetime.
    root.dataset.smartifyUnpersisted = 'true';
    try {
      sessionStorage.setItem(STORAGE_KEY, mode);
      root.dataset.smartifyUnpersisted = 'false';
    } catch (error) {}
    document.dispatchEvent(new Event(CHANGE_EVENT));
  }

  #sync = () => {
    this.refs.button.setAttribute('aria-pressed', String(document.documentElement.dataset.smartifyMode === 'dark'));
  };

  #followSystem = () => {
    const root = document.documentElement;
    if (['light', 'dark'].includes(root.dataset.smartifyPreference)) return;
    root.dataset.smartifyMode = this.#media?.matches ? 'dark' : 'light';
    document.dispatchEvent(new Event(CHANGE_EVENT));
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    const root = document.documentElement;
    if (root.dataset.smartifyUnpersisted === 'true') {
      this.#followSystem();
      return;
    }
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (['light', 'dark'].includes(saved)) {
        root.dataset.smartifyPreference = saved;
        root.dataset.smartifyMode = saved;
        root.dataset.smartifyUnpersisted = 'false';
        document.dispatchEvent(new Event(CHANGE_EVENT));
        return;
      }
      if (root.dataset.smartifyUnpersisted !== 'true') {
        root.dataset.smartifyPreference = 'system';
        root.dataset.smartifyInteracted = 'false';
      }
    } catch (error) {}
    this.#followSystem();
  };


}

if (!customElements.get('smartify-appearance')) {
  customElements.define('smartify-appearance', SmartifyAppearance);
}
